import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';

import { BeaconApiError } from '@beacon/api-client';

import { App } from './app/App.js';
import './app/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => {
        // Don't retry auth failures — bounce to login instead.
        if (err instanceof BeaconApiError && err.status === 401) return false;
        return count < 2;
      },
    },
  },
  queryCache: new QueryCache({
    onError: (err) => {
      // Centralised 401 handling — redirect to /login if anywhere in
      // the app a query reveals we're not signed in.
      if (
        err instanceof BeaconApiError &&
        err.status === 401 &&
        location.pathname !== '/login'
      ) {
        const next = encodeURIComponent(location.pathname + location.search);
        location.replace(`/login?next=${next}`);
      }
    },
  }),
});

// Dev/staging-only debug handle — lets Playwright tests invalidate
// queries directly to verify the React Query focus path without
// waiting for staleTime. Exposed on:
//   - localhost           (local dev)
//   - *.workers.dev       (staging)
// NOT exposed on production custom domains (e.g. beacon.conducthq.com).
if (typeof window !== 'undefined') {
  const host = window.location.hostname;
  const isDevOrStaging =
    host === 'localhost' || host.endsWith('.workers.dev');
  if (isDevOrStaging) {
    (window as unknown as { __queryClient: QueryClient }).__queryClient =
      queryClient;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
